import React from 'react';
const { useState, useEffect } = React;
import { inputSt, labelSt, cardSt, mutedSt } from '../diagnostic/shared.jsx';
import { coachCall } from '../coaching/shared.jsx';
import { COURSE_TOPICS, COURSE_LEVELS, COURSE_ACCESS } from '../../journeyConfig.js';

// Mi recorrido · the catalog properties of a course (coaching-admin ›
// learningSaveCatalog, validated on the server): topics or skills, level,
// prerequisites, estimated duration, whether it is part of the basic route,
// whether a rule may recommend it, and who can start it. They describe the
// content of the course, never a person. Saved apart from the content.

const chip = (on) => ({ border: `1px solid ${on ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: on ? 'var(--teal-dim)' : '#FFFFFF', color: on ? '#155C54' : 'var(--globant-text)', borderRadius: 999, padding: '0 12px', minHeight: 34, font: 'inherit', fontSize: 13, cursor: 'pointer' });
const ERRORS = {
  fields_pending: 'Faltan los campos del catálogo en Coaching Courses (pedido a Omni: docs/coaching/pedido-omni-mi-recorrido.md). El curso se sigue guardando como siempre.',
  'invalid_input: advanced in basic path': 'Un curso avanzado no puede ser parte de la ruta básica.',
  'invalid_input: invalid prerequisite': 'Un requisito previo debe ser otro curso existente.',
  'invalid_input: invalid minutes': 'La duración va en minutos, entre 1 y 6000.',
};

export default function CatalogEditor({ course, onSaved }) {
  const [form, setForm] = useState(course.catalog);
  const [others, setOthers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { setForm(course.catalog); }, [course.id, JSON.stringify(course.catalog)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { coachCall({ action: 'learningOverview' }).then(r => setOthers(r.courses.filter(c => c.id !== course.id))).catch(() => setOthers([])); }, [course.id]);
  const set = (k, v) => { setForm({ ...form, [k]: v }); setMsg(''); };
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);
  const dirty = JSON.stringify(form) !== JSON.stringify(course.catalog);
  const save = async () => {
    setBusy(true); setError(''); setMsg('');
    try { const r = await coachCall({ action: 'learningSaveCatalog', id: course.id, catalog: form }); onSaved?.(r); setMsg('Propiedades guardadas.'); }
    catch (e) { setError(ERRORS[e.message] || e.message); }
    setBusy(false);
  };
  return (
    <section style={cardSt} aria-labelledby="catalog-title">
      <h3 id="catalog-title" style={{ margin: 0, fontSize: 16 }}>Catálogo y recomendaciones</h3>
      <p style={{ ...mutedSt, margin: '2px 0 10px' }}>Describen el contenido del curso, no a las personas. Las usan la ruta básica y las reglas de recomendación de Mi recorrido.</p>

      <span style={labelSt}>Temas o habilidades</span>
      <div role="group" aria-label="Temas o habilidades" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 12px' }}>
        {COURSE_TOPICS.map(t => <button key={t.key} type="button" aria-pressed={form.topics.includes(t.key)} style={chip(form.topics.includes(t.key))} onClick={() => toggle('topics', t.key)}>{form.topics.includes(t.key) ? '✓ ' : ''}{t.label}</button>)}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Nivel</span>
          <select style={inputSt} value={form.level || ''} onChange={e => set('level', e.target.value || null)}>
            <option value="">Sin nivel</option>
            {COURSE_LEVELS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
          </select>
        </label>
        <label style={{ flex: '1 1 160px' }}><span style={labelSt}>Duración estimada (minutos)</span>
          <input style={inputSt} type="number" min={1} max={6000} value={form.minutes ?? ''} onChange={e => set('minutes', e.target.value === '' ? null : Number(e.target.value))} />
        </label>
        <label style={{ flex: '2 1 240px' }}><span style={labelSt}>Quién puede empezarlo</span>
          <select style={inputSt} value={form.access} onChange={e => set('access', e.target.value)}>
            {COURSE_ACCESS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          <span style={{ ...mutedSt, fontSize: 12 }}>{COURSE_ACCESS.find(a => a.key === form.access)?.hint}</span>
        </label>
      </div>

      <span style={{ ...labelSt, marginTop: 12, display: 'block' }}>Requisitos previos</span>
      <div style={{ ...mutedSt, fontSize: 12 }}>No se recomienda hasta que la persona haya terminado estos cursos.</div>
      <div role="group" aria-label="Requisitos previos" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 12px' }}>
        {!others.length ? <span style={mutedSt}>No hay otros cursos.</span> : others.map(c => <button key={c.id} type="button" aria-pressed={form.prerequisites.includes(c.id)} style={chip(form.prerequisites.includes(c.id))} onClick={() => toggle('prerequisites', c.id)}>{form.prerequisites.includes(c.id) ? '✓ ' : ''}{c.name}</button>)}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={form.basicPath} disabled={form.level === 'advanced'} onChange={e => set('basicPath', e.target.checked)} />
          Parte de la ruta básica {form.level === 'advanced' && <span style={mutedSt}>(no para un curso avanzado)</span>}
        </label>
        {form.basicPath && <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginLeft: 26 }}>Orden en la ruta básica
          <input style={{ ...inputSt, width: 90 }} type="number" min={0} max={999} value={form.basicOrder ?? ''} onChange={e => set('basicOrder', e.target.value === '' ? null : Number(e.target.value))} /></label>}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={form.autoRecommend} onChange={e => set('autoRecommend', e.target.checked)} />
          Puede recomendarse automáticamente (según las reglas de Mi recorrido)
        </label>
      </div>

      {error && <div style={{ ...mutedSt, color: 'var(--globant-warning)', marginTop: 8 }} role="alert">{error}</div>}
      {msg && <div style={{ ...mutedSt, color: 'var(--globant-green)', marginTop: 8 }} role="status">{msg}</div>}
      <button className="action-btn btn-primary" style={{ marginTop: 10 }} disabled={busy || !dirty} onClick={save}>{busy ? 'Guardando…' : 'Guardar propiedades'}</button>
    </section>
  );
}
